import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { FiLock, FiDownload, FiFile, FiAlertCircle, FiLoader, FiCheckCircle } from 'react-icons/fi';
import { api } from '../config/api';

interface SharedDocument {
  id: number;
  filename: string;
  file_size: number;
}

interface ShareInfo {
  documents: SharedDocument[];
  expiresAt: string;
  createdByEmail: string;
}

const SharedDocuments: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [pin, setPin] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState('');
  const [shareInfo, setShareInfo] = useState<ShareInfo | null>(null);
  const [downloadComplete, setDownloadComplete] = useState(false);

  const handleVerify = async () => {
    if (pin.length !== 6) {
      setError('Please enter a 6-digit PIN');
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      const response = await api.get(`/document-share/shared/${token}`, {
        params: { pin }
      });

      setShareInfo({
        documents: response.data.documents,
        expiresAt: response.data.expires_at,
        createdByEmail: response.data.created_by_email
      });
    } catch (err: any) {
      const detail = err.response?.data?.detail || 'Failed to verify PIN';
      setError(detail);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleDownload = async () => {
    setIsDownloading(true);

    try {
      const response = await api.get(`/document-share/shared/${token}/download`, {
        params: { pin },
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `documents_${new Date().toISOString().split('T')[0]}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setDownloadComplete(true);
    } catch (err: any) {
      const detail = err.response?.data?.detail || 'Failed to download documents';
      setError(detail);
    } finally {
      setIsDownloading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">GPUme.com</h1>
          <p className="text-gray-400">Secure Document Download</p>
        </div>

        <div className="bg-gray-800 rounded-lg shadow-xl overflow-hidden">
          {!shareInfo ? (
            // PIN Entry View
            <div className="p-8">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-indigo-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiLock className="w-8 h-8 text-indigo-400" />
                </div>
                <h2 className="text-xl font-semibold text-white mb-2">
                  Enter PIN to Access Documents
                </h2>
                <p className="text-gray-400 text-sm">
                  Enter the 6-digit PIN that was sent to your email
                </p>
              </div>

              <div className="mb-6">
                <input
                  type="text"
                  value={pin}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setPin(value);
                    setError('');
                  }}
                  placeholder="000000"
                  className="w-full text-center text-3xl font-mono tracking-[0.5em] bg-gray-700 text-white px-4 py-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  maxLength={6}
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 text-red-400 text-sm mb-4 bg-red-500/10 p-3 rounded-lg">
                  <FiAlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <button
                onClick={handleVerify}
                disabled={pin.length !== 6 || isVerifying}
                className={`w-full py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                  pin.length === 6 && !isVerifying
                    ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                    : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                }`}
              >
                {isVerifying ? (
                  <>
                    <FiLoader className="w-5 h-5 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify PIN'
                )}
              </button>
            </div>
          ) : downloadComplete ? (
            // Download Complete View
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiCheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">
                Download Complete!
              </h2>
              <p className="text-gray-400 mb-6">
                Your documents have been downloaded successfully.
              </p>
              <button
                onClick={() => setDownloadComplete(false)}
                className="px-6 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                Download Again
              </button>
            </div>
          ) : (
            // Documents View
            <div>
              <div className="p-6 border-b border-gray-700">
                <h2 className="text-lg font-semibold text-white mb-1">
                  Documents Ready for Download
                </h2>
                <p className="text-gray-400 text-sm">
                  Shared by {shareInfo.createdByEmail}
                </p>
                <p className="text-gray-500 text-xs mt-1">
                  Expires: {formatDate(shareInfo.expiresAt)}
                </p>
              </div>

              <div className="p-4 max-h-64 overflow-y-auto">
                {shareInfo.documents.map((doc, index) => (
                  <div
                    key={doc.id}
                    className={`flex items-center gap-3 p-3 ${
                      index !== shareInfo.documents.length - 1 ? 'border-b border-gray-700' : ''
                    }`}
                  >
                    <FiFile className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm truncate">{doc.filename}</p>
                      <p className="text-gray-500 text-xs">{formatFileSize(doc.file_size)}</p>
                    </div>
                  </div>
                ))}
              </div>

              {error && (
                <div className="px-6 py-3">
                  <div className="flex items-center gap-2 text-red-400 text-sm bg-red-500/10 p-3 rounded-lg">
                    <FiAlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                </div>
              )}

              <div className="p-6 border-t border-gray-700">
                <button
                  onClick={handleDownload}
                  disabled={isDownloading}
                  className={`w-full py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                    isDownloading
                      ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  {isDownloading ? (
                    <>
                      <FiLoader className="w-5 h-5 animate-spin" />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <FiDownload className="w-5 h-5" />
                      Download All ({shareInfo.documents.length} files)
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        <p className="text-center text-gray-500 text-sm mt-6">
          GPUme.com - Real Expense Document Tracker
        </p>
      </div>
    </div>
  );
};

export default SharedDocuments;
